import React from 'react';
import { FieldProps } from 'formik';
import { Box, Chip, Grid } from '@material-ui/core';
import moment, { Moment } from 'moment';
import { DateTimePicker } from '@material-ui/pickers';
import { CustomLabel } from 'components/inputs';
import { AccessTime } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';

const StyledChip = withStyles({
  root: {
    height: 22,
    borderRadius: 12,
    padding: 10,
    backgroundColor: '#e6f4ea',
    border: '1.5px solid #34a853',
    color: '#218838',
    marginRight: 8,
    marginBottom: 8,
  },
  icon: {
    fontSize: 14,
    marginLeft: -4,
  },
  label: { fontSize: 14 },
})(Chip);

type SendAtDatePickerProps = FieldProps & {
  onChange?: (date: Moment | null) => void;
  title: string;
};

const SendAtDatePicker: React.FC<SendAtDatePickerProps> = ({ field, form, meta, title, ...props }) => {
  const now = moment();
  const currentDay = now.day(); // 0=Sunday, 1=Monday..., 6=Saturday
  const { t } = useTranslation();

  // Compute next Monday date
  const daysUntilMonday = (8 - currentDay) % 7 || 7;
  const monday = moment(now).add(daysUntilMonday, 'days').set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  const quickOptions = [5, 6, 0].includes(currentDay)
    ? [
        {
          id: 'monday-morning',
          label: t('pages.lead.admin.sendEmail.inputs.send_at.mondayMorning'),
          getDate: () => monday.clone().set({ hour: 10, minute: 0 }),
        },
        {
          id: 'monday-midday',
          label: t('pages.lead.admin.sendEmail.inputs.send_at.mondayMidday'),
          getDate: () => monday.clone().set({ hour: 13, minute: 0 }),
        },
      ]
    : [
        {
          id: 'next-morning',
          label: t('pages.lead.admin.sendEmail.inputs.send_at.nextMorning'),
          getDate: () => moment(now).add(1, 'day').set({ hour: 10, minute: 0 }),
        },
        {
          id: 'next-midday',
          label: t('pages.lead.admin.sendEmail.inputs.send_at.nextMidday'),
          getDate: () => moment(now).add(1, 'day').set({ hour: 13, minute: 0 }),
        },
      ];

  return (
    <Grid item>
      <Box display="flex" flexDirection="column">
        <CustomLabel title={title} />
        <Box display="flex" mt={1} flexWrap="wrap">
          {quickOptions.map((opt) => (
            <StyledChip
              label={opt.label}
              icon={<AccessTime fontSize="small" />}
              clickable
              onClick={() => form.setFieldValue(field.name, opt.getDate().toDate())}
            />
          ))}
        </Box>

        <DateTimePicker
          {...field}
          {...props}
          value={field.value}
          onChange={(date: Moment | null) => form.setFieldValue(field.name, date?.toDate() ?? null)}
          disablePast
          // SendGrid sendAt only works for 72 hours in the future - https://docs.sendgrid.com/for-developers/sending-email/scheduling-parameters
          maxDate={moment().add(72, 'hours')}
          InputLabelProps={{
            shrink: true,
            // variant: 'outlined',
          }}
          required
          size="medium"
          InputProps={{ style: { fontSize: 18, minHeight: 48, cursor: 'pointer' } }}
        />

        {meta.touched && meta.error && (
          <Box color="error.main" mt={1}>
            {meta.error}
          </Box>
        )}
      </Box>
    </Grid>
  );
};

export default SendAtDatePicker;
